/**
 * Billing and plan page.
 *
 * The page never grants access: it starts a Stripe Checkout session or opens the
 * Stripe customer portal. Entitlements change only when a signature-verified
 * webhook updates the subscription row.
 */

import { useState } from 'react';
import type { Entitlements, QuotaStatus, SubscriptionSummary } from '@core/types';
import { FREE_ENTITLEMENTS, PRO_ENTITLEMENTS } from '@core/plans';
import { Alert, Badge, QuotaMeter, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { api, ApiError } from '../lib/api';
import type { ShareSummary } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { formatBytes, formatDateTime, formatTitleCase } from '../lib/format';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';

function FeatureList({ entitlements }: { entitlements: Entitlements }) {
  return (
    <ul>
      <li>{entitlements.monthlyDiagnoses} diagnoses per calendar month</li>
      <li>Maximum input size {formatBytes(entitlements.maxInputBytes)}</li>
      <li>{entitlements.maxSavedSpecs} saved OpenAPI specification(s)</li>
      <li>
        {entitlements.persistentHistory
          ? 'Persistent diagnosis history'
          : `History retained for ${entitlements.historyRetentionDays} days`}
      </li>
      <li>
        {entitlements.openApiValidation === 'full'
          ? 'Full OpenAPI request and response validation'
          : 'Structural OpenAPI checks only'}
      </li>
      <li className={entitlements.shareableReports ? '' : 'is-excluded'}>
        Shareable private incident reports
      </li>
      <li>{entitlements.exportFormats.map((format) => `${format} export`).join(' and ')}</li>
      <li className={entitlements.priorityProcessing ? '' : 'is-excluded'}>Priority processing</li>
    </ul>
  );
}

export interface BillingProps {
  entitlements: Entitlements;
  quota: QuotaStatus;
  subscription: SubscriptionSummary;
  billing: { available: boolean; reason: string | null };
  onReload: () => void;
}

export function BillingPage({
  entitlements,
  quota,
  subscription,
  billing,
  onReload,
}: BillingProps) {
  const { getToken, user } = useSession();
  const { navigate } = useRouter();
  const [busy, setBusy] = useState<'checkout' | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shares = useAsync(async (token) => api.listShares(token), []);

  const isPro = entitlements.planId === 'pro';

  return (
    <>
      <PageHead
        title="Billing and plan"
        description="Your plan is resolved on the server from the subscription record, which is only ever written by verified Stripe webhooks."
        actions={<QuotaMeter quota={quota} />}
      />

      {!billing.available ? (
        <Alert tone="warning" title="Billing is not configured on this deployment">
          <p>{billing.reason}</p>
          <p className="small">
            The application still runs normally: every account is on the Free plan and upgrade
            buttons are disabled. See docs/stripe-setup.md to enable subscriptions.
          </p>
        </Alert>
      ) : null}

      {quota.paymentIssue ? (
        <Alert tone="error" title="There is a problem with your subscription payment">
          Stripe reported that the latest payment did not succeed. Access is kept during Stripe's
          retry window; update the payment method in the customer portal to avoid an interruption.
        </Alert>
      ) : null}

      {error ? <Alert tone="error">{error}</Alert> : null}

      <div className="card">
        <div className="card__header">
          <h2 style={{ margin: 0 }}>
            Current plan: {entitlements.displayName}{' '}
            <Badge kind={isPro ? 'pro' : 'default'}>{subscription.status}</Badge>
          </h2>
        </div>

        <div className="row small faint" style={{ gap: 16 }}>
          <span>Signed in as {user?.email ?? 'unknown'}</span>
          <span>Stripe customer linked: {subscription.linkedToStripe ? 'yes' : 'no'}</span>
          {subscription.currentPeriodEnd ? (
            <span>
              Current period ends {formatDateTime(subscription.currentPeriodEnd)}
              {subscription.cancelAtPeriodEnd ? ' (cancels at period end)' : ''}
            </span>
          ) : null}
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          {!isPro ? (
            <button
              type="button"
              className="button button--primary"
              disabled={!billing.available || busy !== null}
              onClick={async () => {
                setBusy('checkout');
                setError(null);
                try {
                  const token = await getToken();
                  const result = await api.startCheckout(token);
                  if (result.checkoutUrl) window.location.assign(result.checkoutUrl);
                  else setError('Stripe did not return a checkout URL.');
                } catch (caught) {
                  setError(
                    caught instanceof ApiError
                      ? `${caught.message}${caught.hint ? ` ${caught.hint}` : ''}`
                      : 'Checkout could not be started.',
                  );
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'checkout' ? <Spinner /> : null}
              Subscribe to Pro — ${PRO_ENTITLEMENTS.priceMonthlyUsd?.toFixed(2)}/month
            </button>
          ) : null}

          <button
            type="button"
            className="button"
            disabled={!billing.available || busy !== null || !subscription.linkedToStripe}
            onClick={async () => {
              setBusy('portal');
              setError(null);
              try {
                const token = await getToken();
                const result = await api.openPortal(token);
                if (result.portalUrl) window.location.assign(result.portalUrl);
                else setError('Stripe did not return a portal URL.');
              } catch (caught) {
                setError(
                  caught instanceof ApiError
                    ? `${caught.message}${caught.hint ? ` ${caught.hint}` : ''}`
                    : 'The customer portal could not be opened.',
                );
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'portal' ? <Spinner /> : null}
            Open the Stripe customer portal
          </button>

          <button type="button" className="button--ghost" onClick={onReload}>
            Refresh plan status
          </button>
        </div>

        <p className="small faint" style={{ marginTop: 12 }}>
          Coming back from Stripe does not grant access. The subscription record is updated by a
          webhook, which can take a few seconds — use “Refresh plan status” if the page looks stale.
          Card details are handled entirely by Stripe and never reach this application.
        </p>
      </div>

      <div className="grid grid--2" style={{ marginTop: 24 }}>
        <div className={`plan${isPro ? '' : ' plan--featured'}`}>
          <h3>{FREE_ENTITLEMENTS.displayName}</h3>
          <div className="plan__price">
            $0 <span>forever</span>
          </div>
          <FeatureList entitlements={FREE_ENTITLEMENTS} />
        </div>
        <div className={`plan${isPro ? ' plan--featured' : ''}`}>
          <h3>{PRO_ENTITLEMENTS.displayName}</h3>
          <div className="plan__price">
            ${PRO_ENTITLEMENTS.priceMonthlyUsd?.toFixed(2)} <span>per month</span>
          </div>
          <FeatureList entitlements={PRO_ENTITLEMENTS} />
        </div>
      </div>

      <div className="card">
        <h3>Share links</h3>
        {!entitlements.shareableReports ? (
          <p className="faint">
            Shareable incident reports are a Pro feature. Upgrade above to create revocable private
            links from a diagnosis result.
          </p>
        ) : shares.loading ? (
          <Spinner label="Loading share links" />
        ) : (shares.data?.shares.length ?? 0) === 0 ? (
          <p className="faint">
            No share links yet. Open a diagnosis result and use the share controls to create one.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <caption className="sr-only">Share links</caption>
              <thead>
                <tr>
                  <th scope="col">Created</th>
                  <th scope="col">Diagnosis</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(shares.data?.shares ?? []).map((share: ShareSummary) => (
                  <tr key={share.id}>
                    <td className="small nowrap">{formatDateTime(share.createdAt)}</td>
                    <td className="small">{share.diagnosisSummary ?? '—'}</td>
                    <td className="small nowrap">{formatDateTime(share.expiresAt)}</td>
                    <td className="small">
                      {share.revokedAt ? (
                        <Badge kind="error">revoked</Badge>
                      ) : new Date(share.expiresAt).getTime() < Date.now() ? (
                        <Badge>expired</Badge>
                      ) : (
                        <Badge kind="confirmed">active</Badge>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button
                          type="button"
                          className="button--small"
                          onClick={() => navigate(`/app/diagnoses/${share.diagnosisId}`)}
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          className="button--danger button--small"
                          disabled={Boolean(share.revokedAt)}
                          onClick={async () => {
                            try {
                              const token = await getToken();
                              await api.revokeShare(token, share.id);
                              shares.reload();
                            } catch {
                              setError('The share link could not be revoked.');
                            }
                          }}
                        >
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small faint" style={{ marginTop: 12 }}>
          Only a SHA-256 hash of each token is stored, so a link cannot be re-displayed after
          creation. Shared pages show the sanitized input and the generated report only — never your
          email, Stripe data or internal identifiers — and are marked {formatTitleCase('noindex')}.
        </p>
      </div>
    </>
  );
}
